import {Item, ItemType, Workspace} from '../../../../API';
import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID} from '../fixtures';

// ── Stable IDs ──────────────────────────────────────────────────────────────
const SEC_KITCHEN  = 'demo-sec1-0001-0000-0000-000000000011';
const SEC_TEAM     = 'demo-sec2-0002-0000-0000-000000000012';
const LEAF_PIZZA   = 'demo-leaf-0001-0000-0000-000000000011';
const LEAF_FOOD    = 'demo-leaf-0002-0000-0000-000000000012';
const LEAF_OPENING = 'demo-leaf-0003-0000-0000-000000000013';
const LEAF_SERVICE = 'demo-leaf-0004-0000-0000-000000000014';
const LEAF_UNIFORM = 'demo-leaf-0005-0000-0000-000000000015';

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();

function leaf(id: string, parentId: string, name: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LEAF,
        childIds: '[]',
        parentIds: JSON.stringify([parentId]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function cluster(id: string, name: string, childIds: string[]): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.CLUSTER,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([WORKSPACE_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

// ── Items ────────────────────────────────────────────────────────────────────
export const items: Item[] = [
    cluster(SEC_KITCHEN, 'Kitchen Operations', [LEAF_PIZZA, LEAF_FOOD, LEAF_OPENING]),
    cluster(SEC_TEAM, 'Team Standards', [LEAF_SERVICE, LEAF_UNIFORM]),
    leaf(LEAF_PIZZA,   SEC_KITCHEN, 'Pizza Making Guide'),
    leaf(LEAF_FOOD,    SEC_KITCHEN, 'Food Safety & HACCP'),
    leaf(LEAF_OPENING, SEC_KITCHEN, 'Opening Checklist'),
    leaf(LEAF_SERVICE, SEC_TEAM,    'Customer Service Guide'),
    leaf(LEAF_UNIFORM, SEC_TEAM,    'Uniform & Conduct Policy'),
];

// ── Workspace override ────────────────────────────────────────────────────────
export const workspaceOverride: Partial<Workspace> = {
    childIds: JSON.stringify([SEC_KITCHEN, SEC_TEAM]),
    name: 'SlicePerfect Pizza',
};

// ── ProseMirror document content keyed by itemId ─────────────────────────────
export const docContents: Record<string, any> = {
    [LEAF_PIZZA]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Pizza Making Guide'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Consistent quality starts at the bench. Follow these steps every time — no shortcuts.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Dough'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Pull dough from the cooler and let it rest at room temperature for 30 minutes before stretching.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Stretch by hand to 12 in (regular) or 14 in (large). Do not use a rolling pin — it collapses the air pockets.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check the edge is 1 cm thick. Thinner edges burn before the centre cooks.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Sauce'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Use the portion ladle (4 oz for regular, 5 oz for large). Spread in a spiral leaving a 1.5 cm border.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Never sauce the edge — it burns and sticks to the oven conveyor.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Toppings'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Cheese goes on first, toppings on top. This keeps toppings from burning and seals moisture in.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Use the portion cups for each topping — unevenness looks bad and costs margin.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Maximum 4 toppings per pizza unless the customer explicitly requests more.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Baking'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Oven is set at 280 °C. Conveyor speed: 6 minutes for regular, 7 minutes for large. Do not adjust the conveyor speed without manager approval.'}]},
        ],
    },
    [LEAF_FOOD]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Food Safety & HACCP'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Every team member is personally responsible for food safety. A single lapse can close the store. When in doubt, throw it out.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Temperature log (Critical Control Points)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Fridges: 1–4 °C. Log at opening, midday, and closing using the wall thermometer, not the fridge gauge.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Cooked pizza internal temperature must reach 75 °C. Spot-check one pizza per shift with the probe thermometer.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If a fridge reads above 8 °C for more than 2 hours, escalate to the manager immediately. Do not use the stock.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Allergens'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'The 14 major allergens are listed on the menu board. Gluten (wheat) is in all our dough and most sauces.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'For allergen requests, use dedicated purple cutting boards and utensils stored in the marked drawer.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Always tell the customer: "Our kitchen handles nuts and gluten — we cannot guarantee zero cross-contact."'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Personal hygiene'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wash hands for 20 seconds: after touching raw meat, after using the toilet, after handling money.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Gloves are mandatory when handling ready-to-eat food. Change gloves between tasks.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Report any illness (vomiting, diarrhoea, jaundice) to the manager before starting your shift. You will be sent home.'}]}]},
            ]},
        ],
    },
    [LEAF_OPENING]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Opening Checklist'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Complete every step before unlocking the front door. Tick off in the paper logbook and initial it.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: '1. Safety & compliance (first 10 minutes)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Disable alarm and sign the security log.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check all fridge temperatures. Log in the HACCP book. If any is out of range, call the manager.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Verify yesterday\'s closing team signed off on sanitisation.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: '2. Kitchen setup (20 minutes)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Pull dough from the walk-in. Count portions against today\'s projected orders.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Preheat the oven — it needs 20 minutes to reach 280 °C. Do not start orders before the oven light turns green.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Fill all topping containers. Label any new containers with today\'s date.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Set out portion ladles, portion cups, and pizza screens. Check for damage.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: '3. Front-of-house (10 minutes)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Count cash float and confirm against yesterday\'s closing total.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Power on POS terminals and printers. Print a test receipt.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wipe down counters and display cases. Clean fingerprints off the glass.'}]}]},
            ]},
        ],
    },
    [LEAF_SERVICE]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Customer Service Guide'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'People come back for the pizza but they leave reviews about the team. Make every interaction worth a five-star mention.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'The greeting'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Make eye contact and say: "Welcome to SlicePerfect, what can I get started for you?" — every time, even when it\'s busy. No "yeah?" or "next."'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Upselling (the right way)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: '"Would you like to add a drink to that?" — one prompt, no pressure, move on if they say no.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Suggest the combo meal when a customer orders a large pizza on its own — it is usually cheaper.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Handling complaints'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Listen fully before responding. Never interrupt.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Say: "I\'m sorry about that. Let me fix it for you." Then fix it — remake the pizza or escalate to a manager.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'You can offer a free dessert for a reasonable complaint. Log it on the comp sheet.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If a customer becomes aggressive or abusive, call the manager. You do not have to tolerate it.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Wait times'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Give an honest wait time — underpromise, overdeliver. If the order runs late, visit the customer proactively. Never make them come to you.'}]},
        ],
    },
    [LEAF_UNIFORM]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Uniform & Conduct Policy'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'We are the face of SlicePerfect. What we look like and how we act directly affects whether customers return.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Uniform requirements'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wear the issued SlicePerfect polo shirt and black trousers (or dark jeans — no rips, no logos).'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Non-slip closed-toe shoes only in the kitchen. No sandals or trainers without ankle support.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Hair tied back or covered with the provided cap whenever you are in the kitchen.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Uniform must be clean at the start of every shift. You are responsible for washing it.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'In-shift conduct'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Phones on silent and in your locker during shift hours. Checking your phone on the floor is a written warning on first occurrence.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Breaks are 30 minutes for shifts over 6 hours. Take them in the break room — not behind the counter.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Eating on the floor is not allowed. Staff meals happen in the break room before or after your shift.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Discipline process'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Verbal warning → written warning → final written warning → termination. Serious misconduct (theft, harassment) may skip straight to termination. All disciplinary actions are logged in your HR file.'}]},
        ],
    },
};
